import type { EmptyObject } from 'type-fest';
import { isBlankValue } from './isBlankValue';

function doesObjectHaveKeyWithValue(value: { [key: string]: unknown }, keys: string[], depth: number): boolean {
	if (!keys.length) {
		return false;
	}

	// Evaluate any non-object properties first
	for (const key of keys) {
		const propertyValue = value[key];
		const propertyType = typeof propertyValue;
		if (propertyType !== 'object') {
			const notEmpty = isNotEmpty(propertyValue, depth + 1);
			if (notEmpty) {
				return true;
			}
		}
	}

	// Look at object properties next
	for (const key of keys) {
		const propertyValue = value[key];
		const propertyType = typeof propertyValue;
		if (propertyType === 'object') {
			const notEmpty = isNotEmpty(propertyValue, depth + 1);
			if (notEmpty) {
				return true;
			}
		}
	}

	return false;
}

function isBeyondDepthLimit(value: unknown, depth: number): boolean {
	// Limit how deep we inspect objects
	if (depth > 20) {
		try {
			throw new Error(`isEmpty: depth limit exceeded, object type: [${typeof value}]`);
		} catch (error) {
			console.debug(`isEmpty: depth limit exceeded, object type: [${typeof value}]`, error);
			try {
				console.debug('isEmpty: value causing depth limit exceed:', value);
			} catch {
				console.debug('isEmpty: value causing depth limit exceed could not be logged');
			}
		}
		return true;
	}

	return false;
}

export function isEmptyObject(value: object | null | undefined, depth = 0): value is EmptyObject {
	// Simplest case
	if (value === null || value === undefined) {
		return true;
	}

	// TODO: needs more investigation to see why some objects that should return false right away are hitting the depth limit

	// Limit how deep we inspect objects
	if (isBeyondDepthLimit(value, depth)) {
		return false;
	}

	// Look at object keys first
	const keys = Object.keys(value);
	const objectWithKeys = value as { [key: string]: unknown };
	if (keys.length) {
		const hasKeyWithValue = doesObjectHaveKeyWithValue(objectWithKeys, keys, depth + 1);
		if (hasKeyWithValue) {
			return false;
		}
	}

	// Also look at object properties, since keys may not cover everything
	const properties = Object.getOwnPropertyNames(value);
	// Only look at things that weren't covered by keys
	const filteredProperties = properties.filter((prop) => !keys.includes(prop));

	if (filteredProperties.length) {
		const hasPropertyWithValue = doesObjectHaveKeyWithValue(objectWithKeys, filteredProperties, depth + 1);
		if (hasPropertyWithValue) {
			return false;
		}
	}

	// If we didn't find anything on the object itself, check the prototype
	const valuePrototype = Object.getPrototypeOf(value);

	// Follow the prototype chain (if there is one)
	if (valuePrototype) {
		// If the prototype is not the base Object prototype, check it too
		if (valuePrototype !== Object.prototype) {
			return isEmptyObject(valuePrototype, depth + 1);
		}
	}

	// If we get this far, assume the object is empty
	return true;
}

type EmptyArray = [] | undefined | null;
type _NonEmptyArray<T> = [T, ...T[]];

export function isEmptyArray<T>(values: T[] | undefined | null, depth = 0): values is EmptyArray {
	// Simplest cases
	if (values === null || values === undefined) {
		return true;
	}
	if (values.length === 0) {
		return true;
	}

	// Limit how deep we inspect objects
	if (isBeyondDepthLimit(values, depth)) {
		return false;
	}

	// Not empty if at least one value in the array is not empty
	return !values.some((item) => isNotEmpty(item, depth + 1));
}

type EmptyValue = null | undefined | '' | EmptyObject | EmptyArray;

export function isEmpty(value: unknown, depth = 0): value is EmptyValue {
	// For strings, trim before checking if they're empty
	if (isBlankValue(value)) {
		return true;
	}

	// Don't consider common primitives as empty
	if (
		typeof value === 'function' ||
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean' ||
		value instanceof Date
	) {
		return false;
	}

	// Check for arrays first
	if (Array.isArray(value)) {
		return isEmptyArray(value, depth + 1);
	}

	// Check for objects
	if (value instanceof Object) {
		return isEmptyObject(value, depth + 1);
	}

	return false;
}

// TODO: this need a return type to assert that it's not an empty value
export function isNotEmpty(value: unknown, depth = 0): boolean {
	return !isEmpty(value, depth);
}
